import { supabase } from "./supabase";
import { digitosVerificadoresCnpj, mascararCnpj } from "./mascaras";
import type { ParametrosDas } from "./dasMei";

// O lado de IO do Simulador MEI: o que vem do banco e o que é sorteado.
//
// A divisão com `dasMei.ts` (a regra) e `documentosMei.ts` (os documentos) é a
// de sempre na casa: aqueles dois são puros e têm prova sem navegador; este
// tem rede e `Math.random()`, e por isso não tem regra nenhuma dentro.

export type { ParametrosDas };

/**
 * Os parâmetros do DAS, do banco (000098).
 *
 * Devolve `null` em qualquer falha — inclusive na recusa por falta do card. A
 * tela mostra a mesma mensagem nos dois casos: quem chega aqui sem o Simulador
 * liberado veio pela URL digitada à mão, e não há nada de útil a dizer sobre a
 * diferença entre "sem permissão" e "fora do ar".
 */
export async function lerParametrosDas(): Promise<ParametrosDas | null> {
  const { data, error } = await supabase.rpc("mei_das_parametros");
  if (error) return null;
  const linha = Array.isArray(data) ? data[0] : data;
  if (!linha) return null;
  // O PostgREST devolve `numeric` como STRING para não perder precisão. Somar
  // string em JavaScript concatena: "81.05" + 1 vira "81.051", e o DAS sairia
  // com um valor que parece plausível. A conversão é aqui, na fronteira — a
  // mesma decisão da máscara de moeda.
  const n = (v: unknown) => Number(v ?? 0);
  return {
    salario_minimo: n(linha.salario_minimo),
    aliquota_inss_geral: n(linha.aliquota_inss_geral),
    aliquota_inss_caminhoneiro: n(linha.aliquota_inss_caminhoneiro),
    inss_geral: n(linha.inss_geral),
    inss_caminhoneiro: n(linha.inss_caminhoneiro),
    icms: n(linha.icms),
    iss: n(linha.iss),
    teto: n(linha.teto),
    teto_caminhoneiro: n(linha.teto_caminhoneiro),
  };
}

// ---------------------------------------------------------------------------
// As identidades fictícias
// ---------------------------------------------------------------------------
// Tudo o que é sorteado mora aqui, e não dentro dos documentos: função que
// sorteia não tem prova, e os documentos precisam ter.

/** Dígitos aleatórios, como string. */
function digitos(quantos: number): string {
  let s = "";
  for (let i = 0; i < quantos; i++) s += Math.floor(Math.random() * 10);
  return s;
}

/**
 * Um CNPJ fictício com dígitos verificadores VÁLIDOS, matriz `0001`.
 *
 * Os DV vêm de `digitosVerificadoresCnpj`, a mesma função que `cnpjValido` usa
 * para conferir — e é por isso que o CNPJ emitido no fim da abertura passa
 * quando o aluno o digita na apuração do DAS. Duas cópias da aritmética faziam
 * a tela recusar o número que ela mesma emitiu.
 */
export function gerarCnpjFicticio(): string {
  // A raiz nunca é toda igual: `00000000` daria um CNPJ que `cnpjValido`
  // recusa pela guarda dos dígitos repetidos, e a tela emitiria um documento
  // com um número que ela própria rejeita depois.
  let raiz = digitos(8);
  while (/^(\d)\1{7}$/.test(raiz)) raiz = digitos(8);
  const base = `${raiz}0001`;
  return mascararCnpj(`${base}${digitosVerificadoresCnpj(base)}`);
}

/** O número do DAS no formato do PGMEI: `07.08.NNNNN.NNNNNNN-N`. */
export function gerarNumeroDas(): string {
  return `07.08.${digitos(5)}.${digitos(7)}-${digitos(1)}`;
}

/** O número da apuração — doze dígitos, como o PGMEI mostra. */
export function gerarNumeroApuracao(): string {
  return digitos(12);
}

/** O número do recibo da DASN — catorze dígitos. */
export function gerarNumeroRecibo(): string {
  return digitos(14);
}

/**
 * A linha digitável do DAS.
 *
 * Fiel na FORMA e inútil na função: o documento não traz código de barras nem
 * registro bancário, e o rodapé diz isso com letra. Fidelidade visual é o valor
 * pedagógico — o aluno reconhece o boleto quando o vê —, e é justamente por
 * isso que a marca d'água e o aviso não são configuráveis.
 */
export function gerarLinhaDigitavel(): string {
  return `85800000001 1 ${digitos(11)} ${digitos(1)} ${digitos(11)} ${digitos(1)} ${digitos(11)} ${digitos(1)}`;
}

/** O código de autenticação do CCMEI. */
export function gerarCodigoAutenticacao(): string {
  const bloco = () => Math.random().toString(36).slice(2, 10).toUpperCase().padEnd(8, "X");
  return `${bloco()}-${bloco()}`;
}

// O calendário (meses, datas, anos da DASN e do DAS) mora em `dasMei.ts`, junto
// com a regra: quais anos se pode declarar é REGRA, e módulo provável nesta casa
// tem zero import — este aqui tem rede. Reexportado para as telas importarem de
// um lugar só.
export {
  MESES,
  dataBr,
  dataHoraBr,
  anosDaDasn,
  anosDoDas,
  type AnoDasn,
} from "./dasMei";
